/**
 * How a flat roof is built, asked before anything is generated.
 *
 * Every other template in the catalogue produces the same estimate every time:
 * pick it, and the roof's measurements decide the rest. A TPO roof does not
 * work that way. The same building can be reroofed over a metal deck with the
 * ribs filled, over a concrete deck with the insulation glued because you
 * cannot screw into it, or built new with no tear-off at all — and those are
 * different materials, different labour and a different scope of work, not
 * different quantities of the same thing.
 *
 * So the template asks. Jonathon, 2026-08-31: "before generating any of the tpo
 * estimate roofboard should ask what the type of insulation will be, if the iso
 * will be screwed down or adhered (if it's screwed down what's the length of
 * the screws), if the tpo will be adhered or screwed down", and then across the
 * same conversation: tear-off layers, coping, scuppers, how it is terminated,
 * whether the walls get insulation too, flute fill, and new construction.
 *
 * The answers do three things. They select which template lines are generated
 * (see `lineApplies`), they change the labour rate, and they carry into the
 * scope of work — "whatever's selected in the estimate builder needs to reflect
 * appropriately in the scope of work as well".
 *
 * Nothing here touches money. Rates live on products and the ladder does the
 * arithmetic; this module answers "which lines, and how much roof".
 */

import { DECK_TOKEN_PREFIXES } from "./deck.js";
import { SIDING_TOKEN_PREFIXES } from "./sidingAnswers.js";

/**
 * The roof type a flat template carries, and the test for one.
 *
 * TPO and PVC both record as this — the distinction is the template's name, not
 * its roof type. One constant so the builder, the generator and any screen that
 * asks agree on the string, which is free text on the document and never
 * renamed (CLAUDE.md rule 8).
 */
export const FLAT_ROOF_TYPE = "Flat / Low Slope";

export const isFlatRoofType = (roofType: string | null | undefined): boolean =>
  (roofType ?? "").trim() === FLAT_ROOF_TYPE;

/** How the insulation is held down. */
export const ISO_ATTACHMENTS = ["mechanical", "adhered"] as const;
export type IsoAttachment = (typeof ISO_ATTACHMENTS)[number];

/** How the membrane is held down. */
export const TPO_ATTACHMENTS = ["screwed", "adhered"] as const;
export type TpoAttachment = (typeof TPO_ATTACHMENTS)[number];

/**
 * How the membrane is terminated at an open edge.
 *
 * Not at a wall — a parapet is capped with coping, and coping does the
 * terminating there. These three are what happens on an eave or a rake.
 */
export const TERMINATIONS = ["bar", "kynar", "tpo_metal"] as const;
export type Termination = (typeof TERMINATIONS)[number];

/**
 * What a termination bar is fastened into, which decides what holds it.
 *
 * A bar on a masonry parapet takes masonry anchors; a bar on a wood curb takes
 * wood screws. Jonathon, 2026-09-22: "need wooden screws to populate for
 * termination bar." Two fasteners for the same bar, and it is one or the other
 * — putting both on the order buys a box nobody opens and teaches the crew
 * that the order is approximate.
 *
 * Masonry is the default because it is what the template has always ordered.
 */
export const BAR_FASTENERS = ["masonry", "wood"] as const;
export type BarFastener = (typeof BAR_FASTENERS)[number];

/**
 * The insulation boards, in inches, plus the two that are not ISO.
 *
 * Thickness and layer count are asked separately rather than enumerated
 * together. "Two layers of 2.2" was one option in the first telling and "2
 * layers of 2.5" arrived a message later; a list of combinations runs out, a
 * thickness and a number does not.
 *
 * **The list is what the supplier sells, not what is conceivable.** Heely-Brown
 * quoted six boards on 2026-09-08 and 2.5" was not among them; a 2.0" and a
 * 1/2" high-density were. Jonathon, 2026-09-14: "drop 2.5" iso", "add the 1/2"
 * hd". A thickness nobody can buy is an answer a rep can give that prices at
 * nothing, which is worse than one fewer option.
 *
 * `iso_hd_1` stays for now. It is offered and still unpriced — the September
 * quote is silent on it — so it is on the same list as the caulk and the
 * waterblock, waiting on a second ask rather than removed.
 */
export const INSULATIONS = [
  { key: "iso_1", label: '1" ISO', inches: 1 },
  { key: "iso_1_5", label: '1.5" ISO', inches: 1.5 },
  { key: "iso_2_2", label: '2.2" ISO', inches: 2.2 },
  { key: "iso_2_6", label: '2.6" ISO', inches: 2.6 },
  { key: "iso_hd_0_5", label: '1/2" high-density ISO', inches: 0.5 },
  { key: "iso_hd_1", label: '1" high-density ISO', inches: 1 },
  { key: "coverboard", label: '1/2" fiberboard coverboard', inches: 0.5 },
] as const;

/*
 * Securock is deliberately not on that list.
 *
 * It is a coverboard that goes *over* an ISO, not instead of one — Valvoline
 * Columbia was 2.6" ISO with 1/2" Securock on top. Offering it as a standalone
 * choice would generate a coverboard with no insulation under it, which is a
 * wrong estimate that reads as a deliberate answer.
 *
 * The stack lives on the Versico option instead. Jonathon, 2026-09-01: "2.6"
 * iso with securock can just live on the valvoline option." The general case —
 * a coverboard over any thickness — is real and not yet specced, so it is not
 * guessed at here.
 */
export const VERSICO_STACK_LABEL = "Securock coverboard over the ISO";

/** How thick the Securock over the ISO is. */
export const SECUROCK_INCHES = 0.5;

export type InsulationKey = (typeof INSULATIONS)[number]["key"];

export const insulation = (key: string | null | undefined) =>
  INSULATIONS.find((i) => i.key === key) ?? null;

/** Every answer the builder collects before it generates. */
export interface FlatRoofAnswers {
  /** No tear-off, no layover, and the scope of work says so. */
  newConstruction: boolean;
  /**
   * Filling the ribs of a metal deck to make a flush surface.
   *
   * Only on a metal deck, and it settles the rest of the build: fill, then one
   * layer of 1" ISO, then TPO screwed down, nothing torn off.
   */
  fluteFill: boolean;
  /** How many layers come off. Zero on new construction and on a flute fill. */
  tearoffLayers: number;
  insulation: InsulationKey;
  /** One or two boards. Two are fastened through both at once, not twice. */
  insulationLayers: number;
  isoAttachment: IsoAttachment;
  tpoAttachment: TpoAttachment;
  /** Whether the parapets get insulation as well as membrane. */
  wallInsulation: boolean;
  termination: Termination;
  /** What a termination bar screws into. Only read when `termination` is bar. */
  barFastener: BarFastener;
  /** Inches of girth on the drip edge, for the sheet arithmetic. */
  terminationGirthInches: number;
  coping: boolean;
  /** Inches of girth on the coping. */
  copingGirthInches: number;
  scuppers: boolean;
  /** Screw length in inches. Applies to both the ISO and the membrane. */
  screwInches: number;
  /**
   * A Versico job, which is a different bill of materials rather than a
   * different price for the same one.
   *
   * Versico is a membrane system, not a brand stamped on generic parts: the
   * membrane, the detail membrane, the bonding adhesive and the fasteners are
   * all the system's own, and a warranty depends on them being used together.
   * Jonathon, 2026-08-31: "it can ask us if it's a versico job and if we select
   * it then it can use these materials."
   *
   * Costs come from Heely-Brown's quote for Valvoline Columbia TN, 2026-08-25.
   */
  versico: boolean;
}

/** The screw length nobody has thought about yet. */
export const DEFAULT_SCREW_INCHES = 6;

/**
 * What the builder starts with: a mechanically fastened reroof, one layer off.
 *
 * The commonest job, so it is the one that needs the fewest answers changed.
 */
export function defaultFlatRoofAnswers(): FlatRoofAnswers {
  return {
    newConstruction: false,
    fluteFill: false,
    tearoffLayers: 1,
    insulation: "iso_2_2",
    insulationLayers: 1,
    isoAttachment: "mechanical",
    tpoAttachment: "screwed",
    wallInsulation: false,
    termination: "bar",
    barFastener: "masonry",
    terminationGirthInches: 12,
    coping: false,
    copingGirthInches: 12,
    scuppers: false,
    screwInches: DEFAULT_SCREW_INCHES,
    versico: false,
  };
}

/**
 * The build a new-construction job almost always is.
 *
 * Jonathon, 2026-08-31: "new construction is mostly likely going to have coping
 * and drip edge, scuppers, 2 layers of 2.5" iso, iso mechanically fastened and
 * tpo glued down."
 *
 * It presets 2.6" rather than the 2.5" he said, because 2.5" was dropped from
 * the catalogue on 2026-09-14 when Heely-Brown turned out not to sell it. 2.6"
 * is the nearest board they do sell and the one Valvoline Columbia was built
 * with. Two layers of it is a tenth of an inch more insulation than he asked
 * for, and it is a preset he can change.
 *
 * A preset, not a lock. Every field it sets can still be changed — it is the
 * starting point for a kind of job, in the same spirit as the defaults above.
 */
export function newConstructionAnswers(): FlatRoofAnswers {
  return {
    ...defaultFlatRoofAnswers(),
    newConstruction: true,
    tearoffLayers: 0,
    insulation: "iso_2_6",
    insulationLayers: 2,
    isoAttachment: "mechanical",
    tpoAttachment: "adhered",
    termination: "kynar",
    coping: true,
    scuppers: true,
  };
}

/**
 * The build a flute fill always is.
 *
 * "there's no tear off since the flute fill goes on metal roofs, the flute fill
 * goes in between the ribs to make them a flush surface, then a layer of 1" iso,
 * then tpo is screwed in."
 *
 * A metal deck is the one thing you can always screw into, so the adhered path
 * does not arise here.
 */
export function fluteFillAnswers(): FlatRoofAnswers {
  return {
    ...defaultFlatRoofAnswers(),
    fluteFill: true,
    tearoffLayers: 0,
    insulation: "iso_1",
    insulationLayers: 1,
    isoAttachment: "mechanical",
    tpoAttachment: "screwed",
  };
}

/**
 * The answers a Versico job starts from.
 *
 * Like the other presets, and for the same reason: ticking it describes a way
 * of building rather than one field. The insulation is set to the 2.6" ISO the
 * Valvoline job used, and the Securock over it comes from the template's own
 * `versico` line — so the ISO is ordered off the line that was already there
 * rather than a second one, which `roof_template_items_unique` refuses and
 * which would be the wrong shape anyway ("the same product twice in the same
 * section is always a mistake").
 *
 * The thickness stays a dropdown afterwards. A Versico job on a different
 * building may want a different board under the same coverboard.
 */
export function versicoAnswers(from: FlatRoofAnswers): FlatRoofAnswers {
  return { ...from, versico: true, insulation: "iso_2_6", insulationLayers: 1 };
}

/**
 * The tokens a set of answers satisfies.
 *
 * A template line carries one of these in `applies_when`, or nothing at all to
 * mean "always". Generation keeps the line when its token is in this set, which
 * is how one template produces a mechanically fastened reroof and a glued-down
 * new build without two templates that drift apart.
 */
export function flatRoofTokens(answers: FlatRoofAnswers): ReadonlySet<string> {
  const tokens = new Set<string>();
  tokens.add(`iso:${answers.isoAttachment}`);
  tokens.add(`tpo:${answers.tpoAttachment}`);
  tokens.add(`termination:${answers.termination}`);
  tokens.add(`bar:${answers.barFastener}`);
  tokens.add(`insulation:${answers.insulation}`);
  if (answers.newConstruction) tokens.add("new_construction");
  if (answers.fluteFill) tokens.add("flute_fill");
  if (answers.tearoffLayers > 0) tokens.add("tearoff");
  if (answers.wallInsulation) tokens.add("wall_insulation");
  if (answers.coping) tokens.add("coping");
  if (answers.scuppers) tokens.add("scuppers");
  if (answers.versico) tokens.add("versico");
  return tokens;
}

/**
 * Whether a template line belongs on this estimate.
 *
 * An empty `applies_when` means every build gets it — the membrane, the
 * cleaner, the rags. A line with a token is generated only when the answers
 * produced that token.
 *
 * Unknown tokens are kept rather than dropped. A line whose condition nobody
 * recognises is a line somebody meant to include, and the failure directions
 * are not equal: a stray line on a material order is spotted and deleted, while
 * a missing one is bought on the day.
 */
export function lineApplies(
  appliesWhen: string | null | undefined,
  tokens: ReadonlySet<string>,
): boolean {
  const condition = (appliesWhen ?? "").trim();
  if (condition === "") return true;
  // Commas are AND, so `versico,tpo:adhered` is the Versico bonding adhesive
  // and only on a glued-down job. There is no OR: a line that belongs to two
  // unrelated builds is two lines, which reads better on the template than a
  // condition somebody has to parse.
  return condition
    .split(",")
    .map((part) => part.trim())
    .filter((part) => part !== "")
    .every((part) => oneCondition(part, tokens));
}

function oneCondition(part: string, tokens: ReadonlySet<string>): boolean {
  const negated = part.startsWith("!");
  const token = negated ? part.slice(1) : part;
  if (!KNOWN_TOKEN_PREFIXES.some((p) => token.startsWith(p))) return true;
  return negated ? !tokens.has(token) : tokens.has(token);
}

const KNOWN_TOKEN_PREFIXES = [
  // The metal panel questions share this matcher. See exposedFastener.ts —
  // a prefix missing from this list is silently *kept* rather than dropped,
  // which on `finish:painted` would put touch-up paint on a galvalume roof.
  "finish:",
  "furring",
  "iso:",
  "tpo:",
  "termination:",
  "bar:",
  "insulation:",
  "new_construction",
  "flute_fill",
  "tearoff",
  "wall_insulation",
  "coping",
  "scuppers",
  "versico",
  // The siding questions share this matcher too (siding.ts).
  ...SIDING_TOKEN_PREFIXES,
  ...DECK_TOKEN_PREFIXES,
];

/* -------------------------------------------------------------------------
 * The roof itself
 * ---------------------------------------------------------------------- */

/** A square is 100 square feet. */
const SQUARE_FEET = 100;

/**
 * The flat area of a roof, which is not always in the flat column.
 *
 * `flat_squares` is filled in when a measurement report says "total flat area"
 * — a house with a flat porch, where the pitched roof is the main event and
 * the flat bit is the exception. A building that is *all* flat does not read
 * that way. GAF's QuickMeasure prints one "Total Roof Area" and a pitch of
 * 1/12, and it lands in `squares` like any other roof, because as far as the
 * report is concerned that is the roof.
 *
 * Job 1060 is what this costs. 15.35 squares in `squares`, nothing in
 * `flat_squares`, and every TPO line measured by the square generated a
 * quantity of nothing and was dropped: no membrane, no insulation, no
 * adhesive, no fasteners, no caulk. Six lines came out of a twenty-line
 * template — the six measured off the eave and the rake, which were filled in
 * — and the order looked plausible enough that it had to be typed out by hand
 * before anybody worked out why.
 *
 * So a flat template asks for the flat area and, finding none, takes the roof
 * area: on a job somebody is pricing as TPO, the roof IS the flat roof. The
 * fallback only ever fires when the flat column is empty, so a genuinely mixed
 * roof — 30 squares of shingle with 3 of flat porch — is untouched, and no
 * pitched template reads this at all.
 */
export function flatSquaresOf(measurement: {
  flat_squares?: unknown;
  squares?: unknown;
}): number {
  const flat = num(measurement.flat_squares);
  return flat > 0 ? flat : num(measurement.squares);
}

/**
 * Squares of parapet wall: how tall, times how long.
 *
 * The membrane always runs up the wall — "you will always adhere tpo to the
 * walls" — so the wall is roof as far as the TPO order is concerned, and it has
 * to be measured before there is enough of it.
 */
export function wallSquares(
  wallLengthLf: number | null | undefined,
  wallHeightFt: number | null | undefined,
): number {
  const length = num(wallLengthLf);
  const height = num(wallHeightFt);
  if (length <= 0 || height <= 0) return 0;
  return (length * height) / SQUARE_FEET;
}

/**
 * What the three area figures on a flat roof are, and why they differ.
 *
 * Not one number, because three different things are bought against it:
 *
 *  - **membrane** is the roof plus the walls. Always.
 *  - **labour** is the roof plus the walls too. Jonathon was asked directly
 *    whether the walls should reach the crew's $100 a square, since a building
 *    with 4' parapets all round would pay for them: "yes it does."
 *  - **insulation** is the roof alone, unless somebody says otherwise. "just
 *    have insulation stick to the roof. you can ask the rep before generating
 *    the estimate if they want the iso or fiberboard on the walls too."
 *  - **flute fill** is the roof alone, always — it fills the ribs of a deck,
 *    and a parapet has no deck. "it's based on just the roof area. doesn't go
 *    on the walls."
 */
export interface FlatRoofAreas {
  /** Roof only, in squares. */
  roofSquares: number;
  /** Parapet walls, in squares. */
  wallSquares: number;
  /** What the membrane and the crew are measured against. */
  membraneSquares: number;
  /** What the insulation is measured against. */
  insulationSquares: number;
  /** What the flute fill is measured against. Never includes walls. */
  fluteFillSquares: number;
}

export function flatRoofAreas(
  measurement: {
    flat_squares?: unknown;
    squares?: unknown;
    wall_length_lf?: unknown;
    wall_height_ft?: unknown;
  },
  answers: Pick<FlatRoofAnswers, "wallInsulation">,
): FlatRoofAreas {
  const roof = flatSquaresOf(measurement);
  const walls = wallSquares(
    num(measurement.wall_length_lf),
    num(measurement.wall_height_ft),
  );
  return {
    roofSquares: roof,
    wallSquares: walls,
    membraneSquares: roof + walls,
    insulationSquares: answers.wallInsulation ? roof + walls : roof,
    fluteFillSquares: roof,
  };
}

/* -------------------------------------------------------------------------
 * Bent metal
 * ---------------------------------------------------------------------- */

/** A sheet of coil is four feet by ten. */
export const SHEET_WIDTH_INCHES = 48;
export const SHEET_LENGTH_FEET = 10;
/** The allowance on coping and drip edge. Jonathon: "10% waste factor." */
export const METAL_WASTE_PCT = 10;

/**
 * How many feet of finished metal come off one 4x10 sheet.
 *
 * Add up the bends into a girth, see how many strips of that width fit across
 * 48 inches, and each strip is ten feet long. The division is floored and the
 * remainder is scrap — a 14" girth gives three strips and throws away six
 * inches of every sheet, which is Jonathon's "sometimes there can be a lot of
 * waste".
 *
 * A 12" girth giving 40 feet is the number he quoted for Kynar termination, and
 * it is what pins this function.
 */
export function feetPerSheet(girthInches: number | null | undefined): number {
  const girth = num(girthInches);
  if (girth <= 0 || girth > SHEET_WIDTH_INCHES) return 0;
  return Math.floor(SHEET_WIDTH_INCHES / girth) * SHEET_LENGTH_FEET;
}

/**
 * Sheets of coil for a run of coping or drip edge.
 *
 * Waste goes on the linear feet before the sheets are worked out, not after —
 * ten percent of a sheet is not a thing you can buy, and rounding twice buys a
 * sheet that nothing asked for.
 */
export function metalSheets(
  linearFeet: number | null | undefined,
  girthInches: number | null | undefined,
  wastePct: number = METAL_WASTE_PCT,
): number {
  const feet = num(linearFeet);
  const perSheet = feetPerSheet(girthInches);
  if (feet <= 0 || perSheet <= 0) return 0;
  const waste = Number.isFinite(Number(wastePct)) ? Number(wastePct) : 0;
  return Math.ceil((feet * (1 + waste / 100)) / perSheet);
}

/** Which girth answer sizes a template line. */
export type GirthSource = "termination" | "coping";

/**
 * The quantity for a line whose size comes from a girth rather than a coverage.
 *
 * Coping runs along the parapet, drip edge along the open edge, and both are
 * cut from the same 4x10 sheet to whatever profile the job needs. A product
 * cannot hold that: the same coil is 40 feet of one profile and 30 of another,
 * so the girth is a fact about the job and the line only says which one to ask.
 *
 * Returns null rather than zero when there is nothing to work from, so the
 * caller can leave the quantity for a person instead of ordering none.
 */
export function girthQuantity(
  source: GirthSource,
  answers: FlatRoofAnswers,
  measurement: {
    eave_lf?: unknown;
    rake_lf?: unknown;
    wall_length_lf?: unknown;
  },
): number | null {
  const run =
    source === "coping"
      ? num(measurement.wall_length_lf)
      : num(measurement.eave_lf) + num(measurement.rake_lf);
  const girth =
    source === "coping"
      ? answers.copingGirthInches
      : answers.terminationGirthInches;
  if (run <= 0 || num(girth) <= 0) return null;
  const sheets = metalSheets(run, girth);
  return sheets > 0 ? sheets : null;
}

/* -------------------------------------------------------------------------
 * Fasteners
 * ---------------------------------------------------------------------- */

/** An insulation board is four feet by eight. */
export const BOARD_SQUARE_FEET = 32;
/** Eight screws and eight plates to a board. */
export const SCREWS_PER_BOARD = 8;

/**
 * Boards of insulation for an area, and the screws that hold them.
 *
 * Two layers are fastened through both at once. Jonathon, asked whether a
 * double layer takes eight screws or sixteen: "at once." So the screw count
 * follows the footprint, not the board count — sixteen would be a second set of
 * fasteners nobody drives.
 */
export function insulationBoards(squares: number, layers: number): number {
  const area = num(squares);
  const stack = Math.max(1, Math.round(num(layers) || 1));
  if (area <= 0) return 0;
  return Math.ceil((area * SQUARE_FEET * stack) / BOARD_SQUARE_FEET);
}

export function insulationScrews(squares: number): number {
  const area = num(squares);
  if (area <= 0) return 0;
  return Math.ceil((area * SQUARE_FEET) / BOARD_SQUARE_FEET) * SCREWS_PER_BOARD;
}

/* -------------------------------------------------------------------------
 * The two warnings
 * ---------------------------------------------------------------------- */

/** How thick a deck is, at the thick end. Jonathon: "3/4" to 1 1/4"." */
export const DECK_INCHES_MAX = 1.25;
/** How far past the deck a screw has to come out. */
export const DECK_PENETRATION_INCHES = 1;

/**
 * The shortest screw that will reach, in inches.
 *
 * Everything the screw passes through, plus the deck, plus an inch out the far
 * side. The deck is taken at its thick end so the warning errs towards being
 * raised — a screw flagged that would have been fine costs a moment, a screw
 * that stops inside the deck holds nothing.
 *
 * "screws always have to penetrate the decking by 1". so keep in mind that
 * metal decking has high ribs and low ribs so the screw still needs to go 1"
 * past the lowest point of the decking (low rib)." The rib depth is not
 * measured anywhere, so this is the floor rather than the answer, and the
 * warning says so.
 */
export function minimumScrewInches(answers: FlatRoofAnswers): number {
  // A Versico job brings its own stack and ignores the dropdown: 2.6" of ISO
  // with a 1/2" Securock coverboard over it, which is what the screw has to
  // pass through before it even reaches the deck.
  const boards =
    (insulation(answers.insulation)?.inches ?? 0) *
    Math.max(1, Math.round(answers.insulationLayers || 1));
  // A Versico job puts a Securock coverboard over the ISO, and the screw has
  // to pass through that too before it reaches the deck.
  const stack = boards + (answers.versico ? SECUROCK_INCHES : 0);
  return stack + DECK_INCHES_MAX + DECK_PENETRATION_INCHES;
}

export interface FlatRoofWarning {
  key: string;
  message: string;
}

/**
 * What to say before generating. Warnings, never blocks.
 *
 * Both of these are things a rep can be right about and the rule wrong about,
 * which is why neither stops the estimate. Jonathon on the first: "it should
 * red flag it but if we're gluing down iso we're almost always going to glue
 * down tpo too."
 */
export function flatRoofWarnings(
  answers: FlatRoofAnswers,
): readonly FlatRoofWarning[] {
  const out: FlatRoofWarning[] = [];

  if (
    answers.isoAttachment === "adhered" &&
    answers.tpoAttachment === "screwed"
  )
    out.push({
      key: "adhered_iso_screwed_tpo",
      message:
        "The insulation is glued but the membrane is screwed. Insulation is " +
        "usually glued because the deck is concrete and cannot be screwed " +
        "into — in which case the membrane cannot be either.",
    });

  const needed = minimumScrewInches(answers);
  if (answers.screwInches > 0 && answers.screwInches < needed)
    out.push({
      key: "screw_too_short",
      message:
        `A ${trim(answers.screwInches)}" screw will not reach. This build is ` +
        `${trim(needed - DECK_INCHES_MAX - DECK_PENETRATION_INCHES)}" of ` +
        `insulation, and a screw has to pass the deck and come out 1" the ` +
        `far side — at least ${trim(needed)}".`,
    });

  return out;
}

/**
 * What the roof has not been told, said before it generates rather than after.
 *
 * The other two warnings are about the build. These are about the measurement,
 * and they matter more, because a missing measurement does not produce a wrong
 * quantity — it produces no line at all, and a line that is absent from a
 * twenty-line order is the hardest thing on this screen to notice.
 *
 * Job 1060 is the case. Nothing measured as flat area and no parapet run, so
 * everything bought by the square and every foot of coping fell off the order
 * silently. `flatSquaresOf` now stops the first of those happening; the
 * parapet has nowhere to fall back to, because a building with no wall run
 * recorded and a building with no parapet look identical from here.
 *
 * Warnings, never blocks — the rep may be about to type the number in, and on
 * a roof with no parapet at all the second one is simply not raised.
 */
export function flatRoofMeasurementWarnings(
  answers: Pick<FlatRoofAnswers, "coping">,
  roofs: readonly {
    flat_squares?: unknown;
    squares?: unknown;
    wall_length_lf?: unknown;
  }[],
): readonly FlatRoofWarning[] {
  const out: FlatRoofWarning[] = [];
  const squares = roofs.reduce((total, roof) => total + flatSquaresOf(roof), 0);
  const wall = roofs.reduce(
    (total, roof) => total + num(roof.wall_length_lf),
    0,
  );

  if (squares <= 0)
    out.push({
      key: "no_area",
      message:
        "This job has no roof area measured, so nothing bought by the square " +
        "will be ordered — no membrane, insulation, adhesive or fasteners. " +
        "Add the area on the measurement screen first.",
    });

  if (answers.coping && wall <= 0)
    out.push({
      key: "coping_without_wall",
      message:
        "Coping is ticked but no parapet wall length is measured, so no " +
        "coping metal and no coping labour will be ordered. Add the parapet " +
        "wall feet on the measurement screen.",
    });

  return out;
}

/* -------------------------------------------------------------------------
 * What the customer reads
 * ---------------------------------------------------------------------- */

/**
 * The scope of work these answers describe, a sentence to a step.
 *
 * "whatever's selected in the estimate builder needs to reflect appropriately
 * in the scope of work as well." Built from the answers rather than stored, so
 * a scope of work cannot go on saying the old build after somebody changes one.
 */
export function flatRoofScopeOfWork(
  answers: FlatRoofAnswers,
): readonly string[] {
  const lines: string[] = [];
  const board = insulation(answers.insulation);
  const stated =
    answers.insulationLayers > 1
      ? `${answers.insulationLayers} layers of ${board?.label ?? "insulation"}`
      : (board?.label ?? "insulation");
  const boards = answers.versico
    ? `${stated} with a 1/2" Securock coverboard over it`
    : stated;

  if (answers.newConstruction)
    lines.push(
      "New construction: there is no existing roof to remove and nothing is " +
        "being roofed over.",
    );
  else if (answers.tearoffLayers > 0)
    lines.push(
      `Tear off and dispose of ${answers.tearoffLayers} ` +
        `layer${answers.tearoffLayers === 1 ? "" : "s"} of existing roofing ` +
        `down to the deck.`,
    );
  else lines.push("Install over the existing roof. Nothing is being removed.");

  if (answers.fluteFill)
    lines.push(
      "Fill the ribs of the metal deck to give a flush, continuous surface " +
        "for the insulation to sit on.",
    );

  lines.push(
    answers.isoAttachment === "adhered"
      ? `Install ${boards}, adhered to the deck.`
      : `Install ${boards}, mechanically fastened with ${trim(
          answers.screwInches,
        )}" screws and 3" plates.`,
  );

  if (answers.wallInsulation)
    lines.push("Carry the insulation up the parapet walls.");

  const membrane = answers.versico ? "Versico .060 TPO" : ".060 TPO";
  lines.push(
    answers.tpoAttachment === "adhered"
      ? `Install ${membrane} membrane, fully adhered, with all seams heat welded.`
      : `Install ${membrane} membrane, mechanically fastened with ${trim(
          answers.screwInches,
        )}" screws and plates, with all seams heat welded.`,
  );

  lines.push(
    "Run the membrane up every parapet wall and terminate it at the top.",
  );

  lines.push(
    answers.termination === "bar"
      ? "Terminate the membrane at the eaves and rakes with termination bar, " +
          `fastened with ${
            answers.barFastener === "wood" ? "wood screws" : "masonry anchors"
          } and sealed.`
      : TERMINATION_WORDING[answers.termination],
  );

  if (answers.coping)
    lines.push(
      "Cap the parapet walls with new 26 gauge Kynar coping metal, sealed at " +
        "every joint.",
    );

  if (answers.scuppers)
    lines.push(
      "Fabricate and install new TPO coated metal at the scuppers, heat " +
        "welded to the membrane.",
    );

  lines.push(
    "Install new TPO pipe boots and flash every penetration on the roof.",
  );
  lines.push("Clean up the site and haul away all debris.");
  return lines;
}

const TERMINATION_WORDING: Record<Termination, string> = {
  bar:
    "Terminate the membrane at the eaves and rakes with termination bar, " +
    "fastened and sealed.",
  kynar:
    "Terminate the membrane at the eaves and rakes over new 26 gauge Kynar " +
    'drip edge metal, primed and covered with 6" cover tape.',
  tpo_metal:
    "Terminate the membrane at the eaves and rakes over new TPO coated drip " +
    "edge metal, with the membrane heat welded directly to it.",
};

const num = (value: unknown): number => {
  const n = Number(value ?? 0);
  return Number.isFinite(n) ? n : 0;
};

/** A measurement for a person to read: 6, not 6.00; 2.2, not 2.2000. */
const trim = (value: number): string => String(Number(value.toFixed(2)));
